import { useMemo, useState } from 'react';
import {
  Anchor,
  Button,
  Group,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link } from 'react-router';
import {
  EVRAK_EKSENI_ETIKETI,
  evrakPaketineGirebilirMi,
  readEvrakTipiKurali,
} from '@isg/validation';
import type { LookupItemDetail } from '@isg/shared-types';
import {
  type EvrakPaketi,
  useEvrakPaketiAc,
  useEvrakPaketiGuncelle,
  useEvrakPaketiOnizleme,
  useEvrakPaketiSil,
  useEvrakPaketleri,
  useKapilar,
  useLookup,
} from '../api/queries';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { KapiSebebi, Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';
import { MaliyetOzeti } from './evrak-paketi-parts';
import { QueryStatus } from './parts';

/**
 * YÜKLENİCİ EVRAK PAKETLERİ (MODÜL KARARLARI PAKETİ · D1 — 17.09.2026;
 * Kapsam §14 K-102a · K-102e · K-102j · K-102k).
 *
 * Paket = firma ve kişi evrak tiplerinden oluşan küme. Tanımı yalnız holding
 * yöneticisi yapar (kapı `kapilar.evrakPaketiKapisi`). İçerik düzenlemesi
 * ANINDA yürürlüktedir; bu yüzden *"Uygula"* ancak aynı seçimin maliyet özeti
 * görüldükten sonra açılır (değiştirmeden önce sonucu göster).
 *
 * ⚠️ Seçenek listesi `evrakPaketineGirebilirMi` yükleminden — gizli ve ekipman
 * eksenli kalem pakete girmez; sunucu aynı yüklemle 422'ler.
 */
export function YukleniciEvrakPaketleriScreen(): React.JSX.Element {
  const paketler = useEvrakPaketleri();
  const evrakTipleri = useLookup('evrak_tipi');
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.evrakPaketiKapisi.yazabilir ?? false;
  const secenekler = useMemo(() => kalemSecenekleri(evrakTipleri.data ?? []), [evrakTipleri.data]);

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Anchor component={Link} to="/yukleniciler" size="sm" data-testid="paketlerden-don">
            Yüklenicilere dön
          </Anchor>
          <Title order={3}>Evrak paketleri</Title>
        </Stack>
        {kapilar.data && (
          <KapiSebebi
            durum={kapilar.data.evrakPaketiKapisi}
            nesne="Evrak paketini"
            fiil="tanımlar"
            testId="evrak-paketi-yazamama-sebebi"
          />
        )}
      </Group>
      <QueryStatus queries={[paketler, evrakTipleri]} />
      {paketler.data && (
        <Table data-testid="evrak-paketleri">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Paket</Table.Th>
              <Table.Th>Evraklar</Table.Th>
              <Table.Th>Yüklenici</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paketler.data.map((paket) => (
              <PaketSatiri
                key={paket.id}
                paket={paket}
                yazabilir={yazabilir}
                secenekler={secenekler}
              />
            ))}
            {yazabilir && <YeniPaketSatiri secenekler={secenekler} />}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

interface KalemSecenegi {
  value: string;
  label: string;
}

function kalemSecenekleri(tipler: LookupItemDetail[]): KalemSecenegi[] {
  return tipler
    .filter((tip) =>
      evrakPaketineGirebilirMi({
        gizli: tip.gizli,
        eksen: readEvrakTipiKurali(tip.kurallar).eksen,
      }),
    )
    .map((tip) => ({
      value: tip.id,
      label: `${tip.ad} (${EVRAK_EKSENI_ETIKETI[readEvrakTipiKurali(tip.kurallar).eksen]})`,
    }));
}

function KalemRozetleri({ paket }: { paket: EvrakPaketi }): React.JSX.Element {
  if (paket.kalemler.length === 0) {
    return (
      <Text size="sm" c="dimmed" data-testid="paket-kalemsiz">
        Evrak istemiyor
      </Text>
    );
  }
  return (
    <Group gap={4}>
      {paket.kalemler.map((kalem) => (
        <Rozet key={kalem.evrakTipiId} anlam="bilgi" testId="paket-kalemi">
          {kalem.evrakTipiAd}
          {kalem.eksen === 'kisi' ? ' (kişi)' : ''}
        </Rozet>
      ))}
    </Group>
  );
}

function PaketSatiri({
  paket,
  yazabilir,
  secenekler,
}: {
  paket: EvrakPaketi;
  yazabilir: boolean;
  secenekler: KalemSecenegi[];
}): React.JSX.Element {
  const [duzenleniyor, setDuzenleniyor] = useState(false);
  const sil = useEvrakPaketiSil();
  if (duzenleniyor) {
    return (
      <PaketDuzenleSatiri
        paket={paket}
        secenekler={secenekler}
        onKapat={() => setDuzenleniyor(false)}
      />
    );
  }
  return (
    <>
      <Table.Tr data-testid="evrak-paketi-satiri" data-paket-ad={paket.ad}>
        <Table.Td>
          <Text size="sm" fw={500}>
            {paket.ad}
          </Text>
        </Table.Td>
        <Table.Td>
          <KalemRozetleri paket={paket} />
        </Table.Td>
        <Table.Td data-testid="paket-yuklenici-sayisi">{paket.yukleniciSayisi}</Table.Td>
        <Table.Td>
          {yazabilir && (
            <SatirEylemleri
              birincil={{
                etiket: 'Düzenle',
                onClick: () => setDuzenleniyor(true),
                testId: 'paket-duzenle',
              }}
              digerleri={[
                {
                  etiket: 'Sil',
                  onClick: () => sil.mutate(paket.id),
                  tehlikeli: true,
                  testId: 'paket-sil',
                },
              ]}
            />
          )}
        </Table.Td>
      </Table.Tr>
      {sil.isError && (
        <Table.Tr>
          <Table.Td colSpan={4}>
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="paket-sil-hata">
              {sil.error.message}
            </Text>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

function PaketDuzenleSatiri({
  paket,
  secenekler,
  onKapat,
}: {
  paket: EvrakPaketi;
  secenekler: KalemSecenegi[];
  onKapat: () => void;
}): React.JSX.Element {
  const baslangic = paket.kalemler.map((k) => k.evrakTipiId);
  const [ad, setAd] = useState(paket.ad);
  const [kalemler, setKalemler] = useState<string[]>(baslangic);
  const onizleme = useEvrakPaketiOnizleme();
  const guncelle = useEvrakPaketiGuncelle();
  const kalemDegisti =
    kalemler.length !== baslangic.length || kalemler.some((id) => !baslangic.includes(id));
  const degisti = kalemDegisti || ad.trim() !== paket.ad;
  useKaydedilmemisIcerik(degisti ? 1 : 0, 'evrak paketi');
  // Önizleme YALNIZ bugünkü seçim için geçerlidir — seçim değişince "Uygula" kapanır.
  const onizlenenSecim = onizleme.variables?.evrakTipiIds;
  const onizlemeGuncel =
    onizleme.data !== undefined &&
    onizlenenSecim !== undefined &&
    onizlenenSecim.length === kalemler.length &&
    onizlenenSecim.every((id) => kalemler.includes(id));
  const uygulanabilir = ad.trim().length > 0 && degisti && (!kalemDegisti || onizlemeGuncel);
  // Pakette olan ama artık seçilemeyen (gizlenmiş) kalem listede görünsün diye eklenir.
  const tumSecenekler = [
    ...secenekler,
    ...paket.kalemler
      .filter((k) => !secenekler.some((s) => s.value === k.evrakTipiId))
      .map((k) => ({ value: k.evrakTipiId, label: k.evrakTipiAd })),
  ];

  return (
    <SolukSatir
      testId="paket-duzenle-satiri"
      dokunuldu
      kaydedilebilir={uygulanabilir}
      kaydediliyor={guncelle.isPending}
      kolonSayisi={3}
      onVazgec={onKapat}
      onKaydet={() =>
        guncelle.mutate(
          {
            id: paket.id,
            input: {
              ...(ad.trim() !== paket.ad ? { ad: ad.trim() } : {}),
              ...(kalemDegisti ? { evrakTipiIds: kalemler } : {}),
            },
          },
          { onSuccess: onKapat },
        )
      }
      ikinciKatman={
        <Stack gap="xs">
          {kalemDegisti && (
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                data-testid="paket-onizle"
                loading={onizleme.isPending}
                onClick={() => onizleme.mutate({ id: paket.id, evrakTipiIds: kalemler })}
              >
                Sonucu göster
              </Button>
              {!onizlemeGuncel && (
                <Text size="xs" c="dimmed" data-testid="paket-onizleme-sebebi">
                  Uygulamadan önce değişikliğin sonucunu görün
                </Text>
              )}
            </Group>
          )}
          {onizlemeGuncel && onizleme.data && (
            <MaliyetOzeti ozet={onizleme.data} testId="paket-icerik-maliyet" />
          )}
          {(onizleme.isError || guncelle.isError) && (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="paket-duzenle-hata">
              {(onizleme.error ?? guncelle.error)?.message}
            </Text>
          )}
        </Stack>
      }
    >
      <Table.Td>
        <TextInput
          size="xs"
          aria-label="Paket adı"
          data-testid="paket-duzenle-ad"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <MultiSelect
          size="xs"
          aria-label="Evraklar"
          data-testid="paket-duzenle-kalemler"
          searchable
          comboboxProps={{ withinPortal: true }}
          data={tumSecenekler}
          value={kalemler}
          onChange={setKalemler}
        />
      </Table.Td>
      <Table.Td>{paket.yukleniciSayisi}</Table.Td>
    </SolukSatir>
  );
}

function YeniPaketSatiri({ secenekler }: { secenekler: KalemSecenegi[] }): React.JSX.Element {
  const ac = useEvrakPaketiAc();
  const [ad, setAd] = useState('');
  const [kalemler, setKalemler] = useState<string[]>([]);
  const dokunuldu = ad !== '' || kalemler.length > 0;
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'evrak paketi satırı');
  const sifirla = (): void => {
    setAd('');
    setKalemler([]);
  };
  return (
    <SolukSatir
      testId="paket-soluk-satir"
      dokunuldu={dokunuldu}
      // Boş paket serbest (K-102h) — yalnız ad zorunlu.
      kaydedilebilir={ad.trim().length > 0}
      kaydediliyor={ac.isPending}
      kolonSayisi={3}
      onVazgec={sifirla}
      onKaydet={() =>
        ac.mutate({ ad: ad.trim(), evrakTipiIds: kalemler }, { onSuccess: () => sifirla() })
      }
      ikinciKatman={
        ac.isError ? (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="paket-ac-hata">
            {ac.error.message}
          </Text>
        ) : undefined
      }
    >
      <Table.Td>
        <TextInput
          size="xs"
          placeholder="Paket adı"
          data-testid="paket-ad"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <MultiSelect
          size="xs"
          placeholder="Evraklar"
          data-testid="paket-kalemler"
          searchable
          comboboxProps={{ withinPortal: true }}
          data={secenekler}
          value={kalemler}
          onChange={setKalemler}
        />
      </Table.Td>
      <Table.Td />
    </SolukSatir>
  );
}
